import { useState, useRef, useEffect } from "react";
import { Menu, ChevronDown, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { Language, languageNames } from "@/i18n/translations";

const flagUrls: Record<Language, string> = {
  en: 'https://flagcdn.com/w40/gb.png',
  el: 'https://flagcdn.com/w40/gr.png',
  fr: 'https://flagcdn.com/w40/fr.png',
  de: 'https://flagcdn.com/w40/de.png',
  ar: 'https://flagcdn.com/w40/sa.png',
};

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isRDOpen, setIsRDOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, changeLanguage, t } = useTranslation();

  const navRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();

  // Arabic is offered on the Middle East page (in any language), so visitors
  // can always switch back to the Arabic view from there.
  const isMiddleEastPage = pathname === '/about/vidavo-middle-east';
  const availableLanguages: Language[] =
    isMiddleEastPage ? ['en', 'el', 'fr', 'de', 'ar'] : ['en', 'el', 'fr', 'de'];

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsAboutOpen(false);
        setIsRDOpen(false);
        setIsMenuOpen(false);
        setIsLangOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleRDDropdown = () => {
    setIsRDOpen(!isRDOpen);
    setIsAboutOpen(false);
    setIsLangOpen(false);
  };

  const toggleAboutDropdown = () => {
    setIsAboutOpen(!isAboutOpen);
    setIsRDOpen(false);
    setIsLangOpen(false);
  };

  const toggleLangDropdown = () => {
    setIsLangOpen(!isLangOpen);
    setIsAboutOpen(false);
    setIsRDOpen(false);
  };

  const closeAllDropdowns = () => {
    setIsAboutOpen(false);
    setIsRDOpen(false);
    setIsMenuOpen(false);
    setIsLangOpen(false);
  };
  return <nav className={`fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
    isScrolled ? 'top-0 w-full max-w-none' : 'top-4 w-[95%] max-w-6xl'
  }`} ref={navRef}>
      <div className={`glass-panel px-6 py-4 flex items-center justify-between shadow-lg backdrop-blur-md bg-white transition-all duration-300 ${
        isScrolled ? 'rounded-none' : 'rounded-full'
      }`}>
        <Link to="/" className="flex items-center gap-2" onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          closeAllDropdowns();
        }}>
          <img src="/lovable-uploads/fc26f480-9c94-4be3-b251-a1f837add861.png" alt="Vidavo Logo" className="h-8 w-auto flex-shrink-0" />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {/* Services Link */}
          <Link 
            to="/services" 
            className="text-neutral-700 hover:text-primary transition-colors font-medium"
            onClick={closeAllDropdowns}
          >
            {t.nav.services}
          </Link>
          
          {/* R&D Dropdown */}
          <div className="relative">
            <button 
              onClick={toggleRDDropdown}
              className="flex items-center gap-1 transition-colors font-medium text-neutral-700 hover:text-primary"
            >
              {t.nav.innovation}
              <ChevronDown className={`w-4 h-4 transition-transform ${isRDOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {isRDOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-[100]"
                >
                  <Link to="/rd/research-on-demand" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.rdMenu.researchOnDemand}
                  </Link>
                  <Link to="/rd/eu-national-projects" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.rdMenu.rdPortfolio}
                  </Link>
                  <Link to="/rd/building-innovation" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.rdMenu.buildingInnovation}
                  </Link>
                  <Link to="/rd/digital-transformation" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.rdMenu.digitalTransformation}
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* About Dropdown */}
          <div className="relative">
            <button 
              onClick={toggleAboutDropdown}
              className="flex items-center gap-1 transition-colors font-medium text-neutral-700 hover:text-primary"
            >
              {t.nav.about}
              <ChevronDown className={`w-4 h-4 transition-transform ${isAboutOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {isAboutOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 w-fit min-w-48 bg-white rounded-lg shadow-lg py-2 z-[100]"
                >
                  <Link to="/about/vida24-home-care" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors whitespace-nowrap" onClick={closeAllDropdowns}>
                    {t.aboutMenu.vida24HomeCare}
                  </Link>
                  <Link to="/about/vidavo-eu-belgium" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.aboutMenu.vidavoEU}
                  </Link>
                  <Link to="/about/vidavo-asia" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors whitespace-nowrap" onClick={closeAllDropdowns}>
                    {t.aboutMenu.vidavoIndia}
                  </Link>
                  <Link to="/about/vidavo-middle-east" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors whitespace-nowrap" onClick={closeAllDropdowns}>
                    {t.vidavoMiddleEast.badge}
                  </Link>
                  <Link to="/about/vidavo" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.aboutMenu.vidavoThroughTime}
                  </Link>
                  <Link to="/about/why-us" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.aboutMenu.whyUs}
                  </Link>
                  <Link to="/about/investor-relations" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.aboutMenu.investorRelations}
                  </Link>
                  <Link to="/about/careers" className="block px-4 py-2 text-neutral-700 hover:bg-neutral-100 transition-colors" onClick={closeAllDropdowns}>
                    {t.aboutMenu.careers}
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        <div className="hidden md:flex items-center gap-4">
          {/* Language Switcher */}
          <div className="relative">
            <button 
              onClick={toggleLangDropdown}
              className="flex items-center gap-2 text-neutral-700 hover:text-primary transition-colors font-medium px-3 py-2 rounded-lg hover:bg-neutral-100"
            >
<img src={flagUrls[language]} alt="" className="w-5 h-4 object-cover rounded-sm" />
              <span className="uppercase text-sm">{language}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {isLangOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full right-0 mt-2 w-40 bg-white rounded-lg shadow-lg py-2 z-[100]"
                >
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        changeLanguage(lang);
                        closeAllDropdowns();
                      }}
                      className={`w-full text-left px-4 py-2 transition-colors ${
                        language === lang 
                          ? 'bg-primary/10 text-primary font-semibold' 
                          : 'text-neutral-700 hover:bg-neutral-100'
                      }`}
                    >
                      {languageNames[lang]}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          <a href="mailto:info@vidavo.eu" className="button-secondary-nav">
            {t.nav.getInTouch}
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2 hover:bg-neutral-200/50 rounded-full transition-colors"
          aria-label={isMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="md:hidden glass-panel mt-2 rounded-xl p-4 shadow-lg bg-white" 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex flex-col gap-2">
              {/* Services Link */}
              <Link 
                to="/services" 
                className="text-neutral-700 hover:text-primary transition-colors font-medium px-4 py-3 hover:bg-neutral-100 rounded-lg"
                onClick={closeAllDropdowns}
              >
                {t.nav.services}
              </Link>
              
              {/* Innovation Dropdown */}
              <div>
                <button
                  onClick={toggleRDDropdown}
                  className="w-full flex items-center justify-between text-neutral-700 font-medium px-4 py-3 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  {t.nav.innovation}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isRDOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {isRDOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-4 mt-1 space-y-1">
                        <Link 
                          to="/rd/research-on-demand" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.rdMenu.researchOnDemand}
                        </Link>
                        <Link 
                          to="/rd/eu-national-projects" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.rdMenu.rdPortfolio}
                        </Link>
                        <Link 
                          to="/rd/building-innovation" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.rdMenu.buildingInnovation}
                        </Link>
                        <Link 
                          to="/rd/digital-transformation" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.rdMenu.digitalTransformation}
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* About Dropdown */}
              <div>
                <button
                  onClick={toggleAboutDropdown}
                  className="w-full flex items-center justify-between text-neutral-700 font-medium px-4 py-3 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  {t.nav.about}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isAboutOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {isAboutOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-4 mt-1 space-y-1">
                        <Link 
                          to="/about/vida24-home-care" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.aboutMenu.vida24HomeCare}
                        </Link>
                        <Link 
                          to="/about/vidavo-eu-belgium" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.aboutMenu.vidavoEU}
                        </Link>
                        <Link 
                          to="/about/vidavo-asia" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.aboutMenu.vidavoIndia}
                        </Link>
                        <Link 
                          to="/about/vidavo-middle-east" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.vidavoMiddleEast.badge}
                        </Link>
                        <Link 
                          to="/about/vidavo" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.aboutMenu.vidavoThroughTime}
                        </Link>
                        <Link 
                          to="/about/why-us" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.aboutMenu.whyUs}
                        </Link>
                        <Link 
                          to="/about/investor-relations" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.aboutMenu.investorRelations}
                        </Link>
                        <Link 
                          to="/about/careers" 
                          className="block text-neutral-600 hover:text-primary transition-colors py-2 px-4 hover:bg-neutral-100 rounded-lg"
                          onClick={closeAllDropdowns}
                        >
                          {t.aboutMenu.careers}
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Language Switcher Mobile */}
              <div>
                <button
                  onClick={toggleLangDropdown}
                  className="w-full flex items-center justify-between text-neutral-700 font-medium px-4 py-3 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <img src={flagUrls[language]} alt="" className="w-5 h-4 object-cover rounded-sm" />
                    {languageNames[language]}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {isLangOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-4 mt-1 space-y-1">
                        {availableLanguages.map((lang) => (
                          <button
                            key={lang}
                            onClick={() => {
                              changeLanguage(lang);
                              closeAllDropdowns();
                            }}
                            className={`w-full text-left py-2 px-4 rounded-lg transition-colors ${
                              language === lang 
                                ? 'bg-primary/10 text-primary font-semibold' 
                                : 'text-neutral-600 hover:text-primary hover:bg-neutral-100'
                            }`}
                          >
                            {languageNames[lang]}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <hr className="border-neutral-200 my-2" />
              <a 
                href="mailto:info@vidavo.eu" 
                className="button-secondary-nav w-full text-center block"
                onClick={closeAllDropdowns}
              >
                {t.nav.getInTouch}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>;
};
export default Navigation;